import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, ClipboardList, Users, Menu, Settings, LogOut, Shield, X, Sliders, UserCircle, Building2, FolderKanban, FileSearch, Plug, Package, FileStack, Hand, Flag, MessageSquare, HardHat, ShieldCheck, BookOpen, Home, TrendingUp, Sparkles, Wrench, SlidersHorizontal, Lightbulb, ClipboardCheck, Briefcase } from 'lucide-react';
import { menusAPI, pluginsAPI } from '../utils/api';
import api from '../utils/api';
import NavNode from './NavNode';

// ------------------------------------------------------------------
// Defaults y caché (a nivel de módulo para evitar re-creaciones por render)
// ------------------------------------------------------------------
const DEFAULT_CONFIG = {
  company_name: 'IMAGINA',
  platform_name: 'Business Central',
  logo_url: null,
};

// ------------------------------------------------------------------
// Group headers (agrupadores visuales, NO ítems de menú)
// ------------------------------------------------------------------
// Solo son etiquetas decorativas que aparecen ANTES del ítem raíz
// cuyo LABEL coincide. Se emparejan por `label` (no por `id`) porque
// el sidebar tiene dos fuentes: `DEFAULT_MENU_ITEMS` usa ids estables
// tipo 'menu-admin', pero la API `/api/menus` retorna UUIDs de
// Supabase. El label ("Administración") es idéntico en ambas fuentes.
// Si el ítem asociado se oculta por permisos, el header también.
const NAV_GROUP_HEADERS_BY_LABEL = {
  'Administración': 'Sistema',
};

const DEFAULT_MENU_ITEMS = [
  {
    id: 'menu-dashboard',
    path: '/home',
    icon: 'Home',
    label: 'Inicio',
    roles: ['admin', 'jefe_postventa', 'supervisor'],
    nivel: 1,
    children: [],
  },
  {
    id: 'menu-gestion-proyectos',
    path: null,
    icon: 'FolderKanban',
    label: 'G. de Proyectos',
    roles: ['admin', 'jefe_postventa'],
    nivel: 1,
    children: [
      { id: 'menu-proyectos', path: '/proyectos', icon: 'Building2', label: 'Proyectos', roles: ['admin'] },
      { id: 'menu-proveedores', path: '/proveedores', icon: 'Users', label: 'Proveedores', roles: ['admin', 'jefe_postventa'] },
    ],
  },
  {
    id: 'menu-admin',
    path: null,
    icon: 'Settings',
    label: 'Administración',
    roles: ['admin'],
    nivel: 1,
    children: [
      { id: 'menu-general', path: '/configuracion-general', icon: 'Sliders', label: 'General', roles: ['admin'] },
      { id: 'menu-usuarios', path: '/usuarios', icon: 'Users', label: 'Usuarios', roles: ['admin'] },
      { id: 'menu-roles', path: '/roles', icon: 'Shield', label: 'Roles', roles: ['admin'] },
      { id: 'menu-menus', path: '/menus', icon: 'Menu', label: 'Menús', roles: ['admin'] },
      { id: 'menu-integraciones', path: '/integraciones', icon: 'Plug', label: 'Integraciones', roles: ['admin'] },
      { id: 'menu-parametros', path: '/parametros', icon: 'Sliders', label: 'Parámetros', roles: ['admin'] },
      { id: 'menu-plugins', path: '/plugins', icon: 'Package', label: 'Plugins', roles: ['admin'] },
      { id: 'menu-seguridad', path: '/admin/seguridad', icon: 'ShieldCheck', label: 'Seguridad', roles: ['admin'] },
      { id: 'menu-auditoria', path: '/auditoria', icon: 'FileSearch', label: 'Auditoría', roles: ['admin'] },
    ],
  },
];

// Claves de sessionStorage para render instantáneo en 2ª+ carga de la sesión
const CACHE_KEY_MENUS = 'ibc_sidebar_menus_v5'; // v5: reorden Administración al final + hijos reordenados (08-02-2026)
const CACHE_KEY_MENUS_ROL = 'ibc_sidebar_menus_rol_v1';
const CACHE_KEY_CONFIG = 'ibc_sidebar_config_v1';

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeCache = (key, value) => {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* quota o deshabilitado */ }
};

// ------------------------------------------------------------------
// NavNode se importa desde './NavNode' (extraído para evitar recursión
// infinita del plugin visual-edits al auto-referenciarse en el mismo file).
// ------------------------------------------------------------------

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  // Estado inicial: primero caché de sesión, luego defaults. Nunca `[]`.
  // Invalidar cache si el rol del usuario cambió (evita ver menús de otro rol).
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const cachedRolId = sessionStorage.getItem(CACHE_KEY_MENUS_ROL);
      if (cachedRolId && user?.rol_id && cachedRolId !== user.rol_id) {
        sessionStorage.removeItem(CACHE_KEY_MENUS);
        sessionStorage.removeItem(CACHE_KEY_MENUS_ROL);
        return DEFAULT_MENU_ITEMS;
      }
    } catch { /* ignore */ }
    return readCache(CACHE_KEY_MENUS) || DEFAULT_MENU_ITEMS;
  });
  const [expandedMenus, setExpandedMenus] = useState({});
  const [config, setConfig] = useState(() => readCache(CACHE_KEY_CONFIG) || DEFAULT_CONFIG);

  useEffect(() => {
    // Fix 3: disparamos ambos fetches en paralelo, sin await entre sí.
    loadDynamicMenus();
    loadConfig();

    const handleConfigUpdate = (e) => {
      if (e.detail) {
        setConfig(prev => {
          const next = { ...prev, ...e.detail };
          writeCache(CACHE_KEY_CONFIG, next);
          return next;
        });
      }
    };
    window.addEventListener('configUpdated', handleConfigUpdate);
    return () => window.removeEventListener('configUpdated', handleConfigUpdate);
    // Fix 4: dep sobre `user?.id` (evita re-ejecutar cuando AuthContext sustituye
    // el objeto `user` por uno nuevo con los mismos datos).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadConfig = async () => {
    try {
      const response = await api.get('/configuracion/general');
      if (response.data) {
        setConfig(prev => {
          const next = { ...prev, ...response.data };
          writeCache(CACHE_KEY_CONFIG, next);
          return next;
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadDynamicMenus = async () => {
    try {
      // Fix 2: paralelizar /api/menus + /api/plugins/hooks con Promise.allSettled.
      // allSettled para que un fallo en plugins no bloquee los menús principales.
      const [menusResult, hooksResult] = await Promise.allSettled([
        menusAPI.getAll(),
        pluginsAPI.getHooks(),
      ]);

      const dynamicMenus = menusResult.status === 'fulfilled' ? menusResult.value.data : null;
      const pluginMenuItems = hooksResult.status === 'fulfilled'
        ? (hooksResult.value.data?.menu_items || [])
        : [];

      if (dynamicMenus && dynamicMenus.length > 0) {
        // Construcción recursiva del árbol (soporta N niveles).
        // 1) Filtrar activos; 2) build map por padre_id; 3) anidar.
        const activos = dynamicMenus.filter(m => m.activo);
        const byParent = new Map();
        activos.forEach(m => {
          const key = m.padre_id || '__root__';
          if (!byParent.has(key)) byParent.set(key, []);
          byParent.get(key).push(m);
        });

        const buildNode = (raw) => {
          const children = (byParent.get(raw.id) || [])
            .slice()
            .sort((a, b) => (a.orden || 0) - (b.orden || 0))
            .map(buildNode);
          return {
            id: raw.id,
            // Si tiene hijos, el padre es agrupador (no navegable directo).
            path: children.length > 0 ? null : (raw.ruta === '#' ? null : raw.ruta),
            icon: raw.icono || (children.length > 0 ? 'FolderKanban' : 'ClipboardList'),
            label: raw.nombre,
            roles: raw.roles_permitidos || ['admin'],
            orden: raw.orden,
            children,
          };
        };

        const structuredMenus = (byParent.get('__root__') || [])
          .slice()
          .sort((a, b) => (a.orden || 0) - (b.orden || 0))
          .map(buildNode);

        // Inyectar menús de plugins en el padre correcto (solo si no existen ya en la tabla menus)
        if (pluginMenuItems.length > 0) {
          const findByLabelOrId = (nodes, predicate) => {
            for (const n of nodes) {
              if (predicate(n)) return n;
              if (n.children && n.children.length > 0) {
                const found = findByLabelOrId(n.children, predicate);
                if (found) return found;
              }
            }
            return null;
          };
          const parentLabelMap = {
            'menu-admin': 'Administración',
            'menu-postventa': 'Postventa',
            'menu-proyectos': 'G. de Proyectos',
            'menu-experiencia': 'Experiencia',
          };
          for (const pmi of pluginMenuItems) {
            const targetParent = findByLabelOrId(
              structuredMenus,
              (p) => p.id === pmi.padre_id || p.label === parentLabelMap[pmi.padre_id]
            );
            if (targetParent) {
              if (!targetParent.children) targetParent.children = [];
              const exists = targetParent.children.some(c => c.id === pmi.id || c.path === pmi.route);
              if (!exists) {
                targetParent.children.push({
                  id: pmi.id,
                  path: pmi.route,
                  icon: pmi.icon || 'Package',
                  label: pmi.name,
                  roles: pmi.roles || ['admin'],
                  _isPlugin: true,
                  children: [],
                });
                targetParent.children.sort((a, b) => (a.orden || 100) - (b.orden || 100));
              }
            }
          }
        }

        if (structuredMenus.length > 0) {
          setMenuItems(structuredMenus);
          writeCache(CACHE_KEY_MENUS, structuredMenus);
          try {
            if (user?.rol_id) sessionStorage.setItem(CACHE_KEY_MENUS_ROL, user.rol_id);
          } catch { /* ignore */ }
          try { window.dispatchEvent(new CustomEvent('menusUpdated')); } catch { /* SSR safe */ }
        } else {
          setMenuItems(DEFAULT_MENU_ITEMS);
        }
      } else {
        setMenuItems(DEFAULT_MENU_ITEMS);
      }
    } catch (error) {
      console.error('Error loading menus:', error);
      setMenuItems(DEFAULT_MENU_ITEMS);
    }
  };


  const getIconComponent = (iconName) => {
    const iconMap = {
      'LayoutDashboard': LayoutDashboard,
      'Home': Home,
      'ClipboardList': ClipboardList,
      'ClipboardCheck': ClipboardCheck,
      'Users': Users,
      'Menu': Menu,
      'Settings': Settings,
      'Shield': Shield,
      'ShieldCheck': ShieldCheck,
      'Sliders': Sliders,
      'SlidersHorizontal': SlidersHorizontal,
      'Building2': Building2,
      'FolderKanban': FolderKanban,
      'FileSearch': FileSearch,
      'Plug': Plug,
      'Package': Package,
      'FileStack': FileStack,
      'Hand': Hand,
      'Flag': Flag,
      'MessageSquare': MessageSquare,
      'HardHat': HardHat,
      'BookOpen': BookOpen,
      'TrendingUp': TrendingUp,
      'Sparkles': Sparkles,
      'Wrench': Wrench,
      'Lightbulb': Lightbulb,
      'Briefcase': Briefcase,
    };
    return iconMap[iconName] || LayoutDashboard;
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Auto-expandir ancestros del nodo activo (al cargar o al cambiar de ruta).
  useEffect(() => {
    const ancestorsToOpen = [];
    const visit = (nodes, trail) => {
      for (const n of nodes) {
        const newTrail = [...trail, n.id];
        const isThisActive = n.path && (location.pathname === n.path || location.pathname.startsWith(n.path + '/'));
        if (isThisActive) {
          // Abrir todos los ancestros (no el nodo mismo si es hoja)
          ancestorsToOpen.push(...trail);
          return true;
        }
        if (n.children && n.children.length > 0) {
          if (visit(n.children, newTrail)) return true;
        }
      }
      return false;
    };
    visit(menuItems, []);
    if (ancestorsToOpen.length > 0) {
      setExpandedMenus(prev => {
        const next = { ...prev };
        ancestorsToOpen.forEach(id => { next[id] = true; });
        return next;
      });
    }
  }, [location.pathname, menuItems]);

  // ------------------------------------------------------------------
  // Lógica de visibilidad basada en user.permisos (fuente única de verdad).
  //
  // Reglas (post-fix Luisa/Jefe_Postventa):
  //   1. Administrador → todos los ítems visibles (bypass).
  //   2. Hojas (sin children definidos) → visibles ⇔ existe permiso con
  //      menu_id === item.id && ver === true en user.permisos.
  //   3. Padres agrupadores (con children) → visibles ⇔ al menos un hijo
  //      queda visible tras filtrar. El permiso propio del padre NO
  //      es suficiente si no tiene hijos visibles.
  //   4. Plugins puros (no registrados en tabla `menus`, marcados con
  //      _isPlugin=true) mantienen fallback por `item.roles` legacy solo
  //      si no tienen permiso registrado — su permiso registrado, si
  //      existe, siempre gana.
  //
  // Los `roles_permitidos` provenientes de la tabla `menus` NO se usan
  // como fallback para non-admin: la matriz de permisos por rol es la
  // única fuente de verdad. Esto evita falsos positivos donde un menú
  // aparecía por listar el rol en roles_permitidos aunque el rol no
  // tuviera permiso ver=true en su matriz.
  // ------------------------------------------------------------------
  const isAdmin = user?.rol_nombre === 'Administrador';
  const userPermisos = user?.permisos || [];
  const permisosByMenuId = useMemo(() => {
    const map = new Map();
    userPermisos.forEach(p => {
      if (p?.menu_id) map.set(p.menu_id, p);
    });
    return map;
  }, [userPermisos]);

  const normalizeRole = (role) => {
    if (!role) return '';
    return role.toLowerCase().replace(/[\s_-]/g, '');
  };

  const isVisibleLeaf = (item) => {
    if (isAdmin) return true;
    const permiso = permisosByMenuId.get(item.id);
    if (permiso) return !!permiso.ver;
    // Fallback legacy SOLO para plugins puros (no registrados en tabla menus).
    if (item._isPlugin && Array.isArray(item.roles) && item.roles.length > 0) {
      const userRoleNormalized = normalizeRole(user?.rol_nombre);
      return item.roles.some(role => normalizeRole(role) === userRoleNormalized);
    }
    return false;
  };

  const filteredMenu = useMemo(() => {
    // Filtrado recursivo:
    //   - Nodo hoja (sin children definidos) → visible ⇔ isVisibleLeaf(nodo).
    //   - Nodo agrupador (con children definidos) → visible ⇔ ≥1 hijo visible.
    const filterNode = (node) => {
      const hasDefinedChildren = Array.isArray(node.children) && node.children.length > 0;
      if (hasDefinedChildren) {
        const children = node.children.map(filterNode).filter(Boolean);
        if (children.length === 0) return null; // padre huérfano → ocultar
        return { ...node, children };
      }
      // Hoja verdadera: sin children definidos.
      return isVisibleLeaf(node) ? { ...node, children: [] } : null;
    };
    return menuItems.map(filterNode).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems, permisosByMenuId, isAdmin, user?.rol_nombre]);

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 skin-sidebar flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Header with Logo and Platform Name */}
        <div className="skin-sidebar-header py-3 lg:py-4 pr-3 lg:pr-4 pl-[18px] lg:pl-6 relative">
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden absolute top-3 right-3 p-2 hover:bg-white/40 rounded-md"
          >
            <X size={20} className="skin-text-soft" />
          </button>

          {/* Logo + Platform Name en 2 líneas */}
          <div className="flex items-center gap-3" data-testid="sidebar-header">
            <div className="skin-brand-mark" data-testid="sidebar-logo-tile">
              {config.logo_url ? (
                <img
                  src={config.logo_url}
                  alt={config.company_name || 'Logo'}
                  data-testid="sidebar-logo"
                />
              ) : (
                <span data-testid="sidebar-logo-initials">
                  {(config.company_name || 'IBC').slice(0, 2).toUpperCase()}
                </span>
              )}
            </div>
            <div
              className="flex flex-col items-start min-w-0 skin-brand-name"
              data-testid="sidebar-title"
            >
              {(() => {
                const raw = (config.platform_name || 'Business Central').trim();
                const spaceIdx = raw.indexOf(' ');
                const line1 = spaceIdx > 0 ? raw.slice(0, spaceIdx) : raw;
                const line2 = spaceIdx > 0 ? raw.slice(spaceIdx + 1).trim() : '';
                return (
                  <>
                    <span className="truncate max-w-[140px]">{line1}</span>
                    {line2 && (
                      <span className="truncate max-w-[140px]" style={{ marginTop: '-0.14em' }}>
                        {line2}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-2 lg:px-3 lg:py-2.5 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {filteredMenu.length === 0 && !isAdmin && (
            <div
              className="px-4 py-6 text-xs text-center text-[#888] border border-dashed border-[#E8E8E7] rounded-md"
              data-testid="sidebar-empty-state"
            >
              No tienes módulos asignados.<br />
              Contacta al Administrador para solicitar acceso.
            </div>
          )}
          {filteredMenu.map((item) => {
            const groupHeader = NAV_GROUP_HEADERS_BY_LABEL[item.label];
            return (
              <React.Fragment key={item.id}>
                {groupHeader && (
                  <div
                    role="presentation"
                    data-testid={`nav-group-header-${groupHeader.toLowerCase()}`}
                    className="skin-group-label"
                  >
                    {groupHeader}
                  </div>
                )}
                <NavNode
                  item={item}
                  depth={0}
                  location={location}
                  expandedMenus={expandedMenus}
                  toggleMenu={toggleMenu}
                  handleNavClick={handleNavClick}
                  getIconComponent={getIconComponent}
                />
              </React.Fragment>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="skin-sidebar-footer p-2 lg:p-3">
          <Link
            to="/perfil"
            onClick={handleNavClick}
            className="block mb-1.5 lg:mb-2 p-2 rounded-md hover:bg-white/60 transition-colors group"
          >
            <div className="flex items-center gap-2.5">
              {user?.foto_url ? (
                <img 
                  src={user.foto_url} 
                  alt={user?.nombre}
                  className="w-8 h-8 lg:w-9 lg:h-9 rounded-full object-cover"
                />
              ) : (
                <div className="skin-avatar-gradient w-8 h-8 lg:w-9 lg:h-9 rounded-full text-xs">
                  {(user?.nombre || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-semibold skin-text-ink truncate">
                  {user?.nombre}
                </p>
                <p className="text-[10px] skin-text-soft truncate">{user?.email}</p>
                <p className="text-[10px] capitalize" style={{ color: 'var(--skin-accent-dark)' }}>{user?.rol_nombre}</p>
              </div>
            </div>
          </Link>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-2.5 lg:px-3 py-1.5 text-xs lg:text-sm rounded-md transition-all skin-danger"
          >
            <LogOut size={14} />
            <span className="font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
